/**
 * Constantes centralisées pour toutes les routes de l'application
 * Point unique de vérité pour la navigation et le routing
 */

export const ROUTES = {
  // Routes d'authentification
  AUTH: {
    LOGIN: '/login',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    CALLBACK: '/auth/callback',
  },

  // Routes principales du dashboard
  DASHBOARD: {
    HOME: '/',
    TASKS: '/tasks',
    TRACKING: '/tracking',
  },

  // Routes de gestion des données
  DATA: {
    PROSPECTS: '/prospects',
    CREWS: {
      LIST: '/crews',
      DETAIL: (id: string) => `/crews/${id}`,
    },
    CONTACTS: {
      LIST: '/contacts',
      DETAIL: (id: string) => `/contacts/${id}`,
    },
    VEHICLES: '/vehicles',
    PRODUCTS: '/products',
    SCHOOLS: '/schools',
    ASSOCIATIONS: '/associations',
    DISCOUNTS: '/discounts',
    FORMS: {
      LIST: '/forms',
      SUBMISSIONS: (id: string) => `/forms/${id}/submissions`,
    },
  },

  // Routes publiques
  PUBLIC: {
    FORM: (slug: string) => `/public/forms/${slug}`,
    ADD_TEAMMATE: (crewId: string) => `/public/crews/${crewId}/add-teammate`,
  },

  // Routes d'administration
  ADMIN: {
    USERS: '/admin/users',
    SETTINGS: '/admin/settings',
    ROLES: '/admin/roles',
    EMAIL_TEMPLATES: '/admin/email-templates',
    BED_AUTOMATIONS: '/admin/bed-automations',
    RESERVED_CREW_NUMBERS: '/admin/reserved-crew-numbers',
    ORPHAN_CREW_NUMBERS: '/admin/orphan-crew-numbers',
    PRICING: '/admin/pricing',
    PAYMENT_EXPORTS: '/admin/exports/paiements',
  },

  // Routes d'API (pour les appels futurs)
  API: {
    BASE: '/api',
    AUTH: '/api/auth',
    PROSPECTS: '/api/prospects',
    TEAMS: '/api/teams',
    CONTACTS: '/api/contacts',
    VEHICLES: '/api/vehicles',
    PRODUCTS: '/api/products',
    SCHOOLS: '/api/schools',
    ASSOCIATIONS: '/api/associations',
    DISCOUNTS: '/api/discounts',
    USERS: '/api/users',
  },
} as const;

/**
 * Type helper pour extraire toutes les routes de manière type-safe
 */
export type RouteKeys = keyof typeof ROUTES;
export type AuthRoutes = (typeof ROUTES.AUTH)[keyof typeof ROUTES.AUTH];
export type DashboardRoutes = (typeof ROUTES.DASHBOARD)[keyof typeof ROUTES.DASHBOARD];
export type DataRoutes = (typeof ROUTES.DATA)[keyof typeof ROUTES.DATA];
export type AdminRoutes = (typeof ROUTES.ADMIN)[keyof typeof ROUTES.ADMIN];
export type ApiRoutes = (typeof ROUTES.API)[keyof typeof ROUTES.API];

/**
 * Union type de toutes les routes de l'application
 */
export type AppRoute = AuthRoutes | DashboardRoutes | DataRoutes | AdminRoutes;

/**
 * Utilitaires pour travailler avec les routes
 */
export const RouteUtils = {
  /**
   * Vérifie si une route est une route d'authentification
   */
  isAuthRoute: (path: string): path is AuthRoutes => {
    return Object.values(ROUTES.AUTH).includes(path as AuthRoutes);
  },

  /**
   * Vérifie si une route est une route d'administration
   */
  isAdminRoute: (path: string): path is AdminRoutes => {
    return Object.values(ROUTES.ADMIN).includes(path as AdminRoutes);
  },

  /**
   * Vérifie si une route nécessite une authentification
   */
  requiresAuth: (path: string): boolean => {
    return !RouteUtils.isAuthRoute(path);
  },

  /**
   * Obtient le titre de la page basé sur la route
   */
  getPageTitle: (path: string): string => {
    const titleMap: Record<string, string> = {
      [ROUTES.DASHBOARD.HOME]: 'Tableau de bord',
      [ROUTES.DASHBOARD.TASKS]: 'Mes Tâches',
      [ROUTES.DASHBOARD.TRACKING]: 'Suivi & Inscriptions',
      [ROUTES.DATA.PROSPECTS]: 'Demandes entrantes',
      [ROUTES.DATA.CREWS.LIST]: 'Équipages',
      [ROUTES.DATA.CONTACTS.LIST]: 'Contacts',
      [ROUTES.DATA.VEHICLES]: 'Véhicules',
      [ROUTES.DATA.PRODUCTS]: 'Produits',
      [ROUTES.DATA.SCHOOLS]: 'Écoles',
      [ROUTES.DATA.ASSOCIATIONS]: 'Associations',
      [ROUTES.DATA.DISCOUNTS]: 'Réductions',
      [ROUTES.DATA.FORMS.LIST]: 'Formulaires',
      [ROUTES.ADMIN.USERS]: 'Utilisateurs & rôles',
      [ROUTES.ADMIN.EMAIL_TEMPLATES]: "Modèles d'email",
      [ROUTES.ADMIN.BED_AUTOMATIONS]: 'Automatisations BED',
      [ROUTES.ADMIN.RESERVED_CREW_NUMBERS]: "Numéros d'équipage réservés",
      [ROUTES.ADMIN.ORPHAN_CREW_NUMBERS]: "Numéros d'équipage orphelins",
      [ROUTES.ADMIN.PRICING]: 'Gestion des tarifs',
      [ROUTES.AUTH.LOGIN]: 'Connexion',
      [ROUTES.AUTH.FORGOT_PASSWORD]: 'Mot de passe oublié',
      [ROUTES.AUTH.RESET_PASSWORD]: 'Réinitialiser le mot de passe',
    };

    return titleMap[path] || 'Desertours CRM';
  },
};
